/* eslint-disable react-hooks/exhaustive-deps */
import {
  BottomNavigationAction,
  Box,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { basicNavItems, getMenuItemIconConfig } from "../../../constants/navigationItems";
import { ROUTES } from "../../../constants/routes";
import CreateTransactionIcon from "../icons/CreateTransactionIcon";

const getFirstPathPart = (pathname: string) => {
  const path = pathname.split("/")[1];
  return path ? `/${path}` : ROUTES.DASHBOARD;
};

// Indicator visual sizing relative to the active item's rect:
// shrink vertically (top/bottom inset) and expand horizontally (overhang)
const INDICATOR_VERTICAL_INSET = 6;
const INDICATOR_HORIZONTAL_OVERHANG = 10;
// Minimum distance the indicator keeps from the pill's rounded edges so
// it never sits flush against them (mirrors the vertical inset visually)
const INDICATOR_EDGE_INSET = 8;
const NAV_HEIGHT = 64;
const NAV_INNER_PADDING_X = 16;
const FAB_SIZE = 66;
// Width of the visual gap carved out of the pill to host the centered FAB
const CENTER_GAP = FAB_SIZE + 10;
// How far the FAB rises above the pill's top edge (~1/3 of its height)
const FAB_TOP_PROTRUSION = Math.round(FAB_SIZE / 3);

type IndicatorRect = { left: number; width: number };

const BottomNavigator = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [value, setValue] = useState(getFirstPathPart(location.pathname));

  // Keep the nav below the onboarding overlay (modal + 5) so the dark overlay
  // can darken the pill and its circular cutout reveals the FAB. The overlay
  // uses pointer-events: none, so the FAB stays clickable through it.
  const bottomBarZIndex = theme.zIndex.drawer - 1;

  const navContainerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  const [indicator, setIndicator] = useState<IndicatorRect | null>(null);
  const [hasMeasured, setHasMeasured] = useState(false);

  useEffect(() => {
    setValue(getFirstPathPart(location.pathname));
  }, [location.pathname]);

  const measureIndicator = React.useCallback(() => {
    const container = navContainerRef.current;
    const activeItem = itemRefs.current.get(value);
    if (!container || !activeItem) return;

    const containerRect = container.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();

    // Clamp the indicator to stay within the container bounds, keeping a small
    // inset from the pill's rounded edges so it never sits flush against them
    const rawLeft = itemRect.left - containerRect.left - INDICATOR_HORIZONTAL_OVERHANG;
    const rawWidth = itemRect.width + INDICATOR_HORIZONTAL_OVERHANG * 2;
    const left = Math.max(INDICATOR_EDGE_INSET, rawLeft);
    const right = Math.min(containerRect.width - INDICATOR_EDGE_INSET, rawLeft + rawWidth);
    const width = Math.max(0, right - left);

    setIndicator({ left, width });
  }, [value]);

  // Measure synchronously before paint to avoid flash of misplaced indicator
  useLayoutEffect(() => {
    measureIndicator();
    // After the first measurement, allow transitions
    const id = requestAnimationFrame(() => setHasMeasured(true));
    return () => cancelAnimationFrame(id);
  }, [measureIndicator]);

  // Re-measure on resize (orientation change, window resize, font scaling)
  useEffect(() => {
    const handle = () => measureIndicator();
    window.addEventListener("resize", handle);
    return () => window.removeEventListener("resize", handle);
  }, [measureIndicator]);

  const handleNavItemClick = (newValue: string) => {
    setValue(newValue);
    navigate(newValue);
  };

  const handleAddTransaction = () => {
    navigate(ROUTES.CREATE_BASIC_TRANSACTION);
  };

  const setItemRef = (itemValue: string) => (node: HTMLButtonElement | null) => {
    if (node) {
      itemRefs.current.set(itemValue, node);
    } else {
      itemRefs.current.delete(itemValue);
    }
  };

  return (
    <>
      <Box
        aria-hidden
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          height: "calc(88px + env(safe-area-inset-bottom, 0px))",
          backgroundColor: "transparent",
          zIndex: bottomBarZIndex - 1,
          pointerEvents: "none",
        }}
      />
      <Box
        sx={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: "calc(8px + env(safe-area-inset-bottom, 0px))",
          zIndex: bottomBarZIndex,
          display: "flex",
          justifyContent: "center",
          paddingInline: 1,
          pointerEvents: "none",
        }}
      >
        <Box
          sx={{
            position: "relative",
            width: "100%",
            maxWidth: 460,
          }}
        >
          <Box
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.3)",
              backdropFilter: "saturate(180%) blur(12px)",
              WebkitBackdropFilter: "saturate(180%) blur(12px)",
              borderRadius: 999,
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)",
              pointerEvents: "auto",
              overflow: "hidden",
            }}
          >
            <Box
              ref={navContainerRef}
              sx={{ position: "relative", height: NAV_HEIGHT }}
            >
              {/* Sliding indicator pill — single shared background that animates between items */}
              <Box
                aria-hidden
                sx={{
                  position: "absolute",
                  top: INDICATOR_VERTICAL_INSET,
                  bottom: INDICATOR_VERTICAL_INSET,
                  left: 0,
                  width: indicator?.width ?? 0,
                  transform: `translateX(${indicator?.left ?? 0}px)`,
                  borderRadius: "999px",
                  backgroundColor: alpha(theme.palette.navy[800], 0.08),
                  opacity: indicator ? 1 : 0,
                  transition: hasMeasured
                    ? "transform 360ms cubic-bezier(0.34, 1.56, 0.64, 1), width 360ms cubic-bezier(0.34, 1.56, 0.64, 1), opacity 200ms ease"
                    : "none",
                  willChange: "transform, width",
                  pointerEvents: "none",
                  zIndex: 0,
                }}
              />

              <Box
                role="navigation"
                sx={{
                  position: "relative",
                  display: "flex",
                  alignItems: "stretch",
                  backgroundColor: "transparent",
                  borderRadius: 999,
                  height: NAV_HEIGHT,
                  paddingInline: `${NAV_INNER_PADDING_X}px`,
                  gap: 0.25,
                  zIndex: 1,
                  "& .MuiBottomNavigationAction-root": {
                    flex: 1,
                    minWidth: 0,
                    padding: "4px 8px",
                    color: theme.palette.text.primary,
                    backgroundColor: "transparent !important",
                    transition: "color 200ms ease",
                  },
                  "& .MuiBottomNavigationAction-root.Mui-selected": {
                    color: theme.palette.text.primary,
                  },
                  "& .MuiBottomNavigationAction-label": {
                    marginTop: 0.5,
                    fontSize: 11,
                    fontWeight: 600,
                    lineHeight: 1.2,
                    opacity: 1,
                  },
                  "& .MuiBottomNavigationAction-label.Mui-selected": {
                    fontSize: 11,
                    fontWeight: 600,
                  },
                }}
              >
                {(() => {
                  const visibleItems = basicNavItems.filter((item) => !item.hidden);
                  const midpoint = Math.ceil(visibleItems.length / 2);

                  const renderItem = (item: typeof visibleItems[number]) => {
                    const iconConfig = getMenuItemIconConfig(item.key, theme);
                    const IconComponent = iconConfig.component;
                    const selected = value === item.path;

                    return (
                      <BottomNavigationAction
                        key={item.key}
                        ref={setItemRef(item.path)}
                        className={selected ? "Mui-selected" : undefined}
                        showLabel
                        onClick={() => handleNavItemClick(item.path)}
                        label={
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 11,
                              fontWeight: 600,
                              lineHeight: 1.2,
                              whiteSpace: "nowrap",
                              color: "inherit",
                              ...item.textStyle,
                            }}
                          >
                            {t(item.translationKey, { ns: "navigation" })}
                          </Typography>
                        }
                        icon={<IconComponent {...iconConfig.props} sx={{ fontSize: 27 }} />}
                        sx={{
                          paddingInline: 0.5,
                        }}
                      />
                    );
                  };

                  return [
                    ...visibleItems.slice(0, midpoint).map(renderItem),
                    <Box
                      key="__center-gap"
                      aria-hidden
                      sx={{
                        flex: `0 0 ${CENTER_GAP}px`,
                        width: CENTER_GAP,
                        pointerEvents: "none",
                      }}
                    />,
                    ...visibleItems.slice(midpoint).map(renderItem),
                  ];
                })()}
              </Box>
            </Box>
          </Box>

          <IconButton
            data-onboarding-id="onboarding-add-transaction"
            onClick={handleAddTransaction}
            aria-label={t("bottomNav.addTransaction", { ns: "navigation" })}
            disableRipple
            disableFocusRipple
            sx={{
              position: "absolute",
              left: "50%",
              top: -FAB_TOP_PROTRUSION,
              transform: "translateX(-50%)",
              width: FAB_SIZE,
              height: FAB_SIZE,
              padding: 0,
              borderRadius: "50%",
              backgroundColor: "transparent",
              filter:
                "drop-shadow(0 6px 14px rgba(249, 206, 70, 0.28)) drop-shadow(0 3px 8px rgba(0, 0, 0, 0.08))",
              pointerEvents: "auto",
              transition:
                "transform 260ms cubic-bezier(0.34, 1.56, 0.64, 1), filter 260ms ease",
              "&:hover": {
                backgroundColor: "transparent",
                transform: "translateX(-50%) scale(1.05)",
                filter:
                  "drop-shadow(0 8px 18px rgba(249, 206, 70, 0.32)) drop-shadow(0 4px 10px rgba(0, 0, 0, 0.10))",
              },
              "&:active": {
                backgroundColor: "transparent",
                transform: "translateX(-50%) scale(0.96)",
                filter:
                  "drop-shadow(0 4px 10px rgba(249, 206, 70, 0.22)) drop-shadow(0 2px 6px rgba(0, 0, 0, 0.08))",
              },
              "&.Mui-focusVisible": {
                backgroundColor: "transparent",
              },
            }}
          >
            <CreateTransactionIcon width={FAB_SIZE} height={FAB_SIZE} />
          </IconButton>
        </Box>
      </Box>
    </>
  );
};

export default BottomNavigator;
